import { getCore } from 'cvat-core-wrapper';

export type SceneMode = 'FP' | 'VR';

// a project material, as the server lists it for the material select
export interface SceneMaterial {
    id: number;
    name: string;
    path: string;
    image_path: string | null;
    label_name: string | null;
    room_type: string | null;
}

// what assembly takes for an empty value; missing: the name is not in its file
export interface SceneFallback {
    name: string;
    preview: string | null;
    missing: boolean;
    // the .blend holding it; absent or null when the index does not name one
    file?: string | null;
}

// a shape holding a value other than the item's scene value
export interface SceneOverride {
    id: number;
    frame: number;
    job: number;
    value: string;
}

export interface SceneItem {
    // wall, surface or floor:<room label>; spot, curtain or tule for the models
    id: string;
    // materials are set from the panel, models are shown only (set per object)
    kind: 'material' | 'model';
    count: number;
    // the scene value: the panel's choice, else what most shapes hold; a material or model path,
    // "" for the fallback or no model, null without a choice and shapes
    value: string | null;
    exceptions: number;
    overrides: SceneOverride[];
    modes: SceneMode[];
    candidates: SceneMaterial[];
    fallback: SceneFallback | null;
}

export interface SceneFolders {
    box: boolean;
    rooms: boolean;
    materials: boolean;
}

export interface Scene {
    mode: SceneMode;
    folder: string | null;
    // the folder as Windows users open it, like the project page shows it
    folder_windows: string | null;
    // the project index of this mode exists: project materials and fallback names are known
    indexed: boolean;
    folders: Record<SceneMode, SceneFolders> | null;
    items: SceneItem[];
}

async function call<T>(path: string, method: string, data?: unknown): Promise<T> {
    const core = getCore();
    const response = await core.server.request(`${core.config.backendAPI}${path}`, {
        method,
        ...(data === undefined ? {} : { data }),
    });
    return response.data as T;
}

// without a mode the server answers in the one saved for the task
export function fetchScene(taskId: number, mode?: SceneMode): Promise<Scene> {
    return call<Scene>(`/tasks/${taskId}/scene${mode ? `?mode=${mode}` : ''}`, 'GET');
}

export function saveSceneMode(taskId: number, mode: SceneMode): Promise<Scene> {
    return call<Scene>(`/tasks/${taskId}/scene`, 'POST', { mode });
}

export interface SceneApplyBody {
    mode: SceneMode;
    item: string;
    // a candidate path, or "" for the fallback
    value: string;
    include_exceptions: boolean;
    // only these shapes, and the scene value stays
    shapes?: number[];
}

// sets the material of an item's shapes on the server; answers with the fresh scene
export function applySceneMaterial(taskId: number, body: SceneApplyBody): Promise<Scene> {
    return call<Scene>(`/tasks/${taskId}/scene`, 'POST', body);
}
